import React from 'react';
import { BodyMedium } from './Typography';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Breadcrumb Component
 *
 * Renders a WAI-ARIA compliant breadcrumb trail using an ordered list
 * (<nav aria-label="Breadcrumb"><ol><li>) so that assistive technologies
 * can correctly identify the navigation landmark and item relationships
 * (WCAG 1.3.1 Info and Relationships, 4.1.2 Name, Role, Value).
 *
 * The final (current) item receives aria-current="page" per the ARIA
 * breadcrumb authoring pattern.
 */
const Breadcrumb = ({ items }) => {
  const { themeColors } = useTheme();

  return (
    <nav aria-label="Breadcrumb" className="mb-0">
      <ol className="flex items-center space-x-2 list-none p-0 m-0" style={{ color: themeColors.textSecondary }}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={index} className="flex items-center">
              {/* Breadcrumb Item */}
              {item.onClick && !isLast ? (
                <button
                  onClick={item.onClick}
                  className="transition-colors text-xs"
                  style={{ color: themeColors.textSecondary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = themeColors.textPrimary}
                  onMouseLeave={(e) => e.currentTarget.style.color = themeColors.textSecondary}
                >
                  <BodyMedium className="text-xs" style={{ color: 'inherit' }}>
                    {item.label}
                  </BodyMedium>
                </button>
              ) : (
                /* Current page: no onClick, aria-current="page" */
                <BodyMedium
                  className="text-xs"
                  aria-current={isLast ? 'page' : undefined}
                  style={{ color: themeColors.textSecondary }}
                >
                  {item.label}
                </BodyMedium>
              )}

              {/* Separator — decorative, hidden from screen readers */}
              {!isLast && (
                <svg
                  aria-hidden="true"
                  className="w-3 h-3 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ color: themeColors.textTertiary }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
